/**
 * Depth occlusion against the basemap's 3D content (buildings, models, landmarks).
 *
 * Mapbox makes this opt-in for symbols by the *presence* of the key, not its value:
 * `SymbolStyleLayer` sets `hasOcclusionOpacityProperties` from
 * `'icon-occlusion-opacity' in layer.paint || 'text-occlusion-opacity' in layer.paint`, and only
 * then compiles DEPTH_OCCLUSION for anything other than terrain. So an overlay layer that omits
 * these draws over every building, which is what every one of ours did (STX1-1061). Adding them
 * also moves the layer after the last 3D layer in the merged order and into mapbox's depth pass,
 * so no slotting or `beforeId` of ours is involved.
 *
 * 0 = hidden while occluded. `slot: 'middle'` would look similar but also buries the layer under
 * the basemap's place and POI labels, and `symbol-z-elevate` is rooftop placement, not occlusion.
 *
 * Compose per layer rather than spreading one object: the spec declares `icon-occlusion-opacity`
 * as requiring `icon-image` and `text-occlusion-opacity` as requiring `text-field`, so a layer
 * that draws only one of the two must not carry the other's key.
 *
 * SYMBOL ONLY. `line-occlusion-opacity` looks like a counterpart but is not one: 0 is already its
 * spec default, `hasOcclusionOpacityProperties` is a SymbolStyleLayer field, and every branch in
 * the line renderer is gated on the value being non-zero — so setting it changes nothing. Lines,
 * circles, fills and rasters cannot opt in; the traffic layer gets there with `slot` instead.
 *
 * So the shape fills and the plan overlays stay unoccluded.
 * Mapbox also disables symbol depth occlusion entirely on the globe projection
 * (`depthOcclusionForSymbolsAndCircles` is `projection.name !== 'globe' && !isOrthographic`),
 * so this is mercator-only by their design; mercator is our default.
 */
export const ICON_OCCLUSION_PAINT = { 'icon-occlusion-opacity': 0 }
export const TEXT_OCCLUSION_PAINT = { 'text-occlusion-opacity': 0 }

// The common case: a symbol layer drawing both an icon and its label.
export const ICON_TEXT_OCCLUSION_PAINT = {
  ...ICON_OCCLUSION_PAINT,
  ...TEXT_OCCLUSION_PAINT,
}
